import React from 'react';

import {
  Collapse,
  CollapseProps,
  Flex,
  Popconfirm,
  Space,
  Table,
  Tooltip,
} from 'antd-v5';
import { chain, get, isNil, noop } from 'lodash';
import {
  MdAddCircleOutline,
  MdDelete,
  MdDeleteOutline,
  MdEdit,
  MdInfo,
} from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Text } from 'scouting-ui/lib';
import { IconButton } from 'scouting-ui/lib/Button';

import type { MBCYouthAssignationPayload } from '@appTypes/esb';
import { useMBDict } from '@dict/duck/hooks';
import {
  useAssignMBCToYouthMutation,
  useRemoveMBCYouthMBMutation,
} from '@modules/meritBadgeCounselors/api';
import tagFactory from '@modules/rtk/utils/tagFactory';
import { AdvancementStatusTag } from '@shared/components/AdvancementStatusTag';
import { advancementTypes } from '@shared/constants';
import { goToAdvancementDetails } from '@shared/duck/actionsTyped';
import { isMobileSel } from '@shared/duck/selectors/general.selectors';
import { getPreferredName } from '@shared/utils/preferredName';
import { toastService } from '@toasts';

import { useYouthGroupedByClass } from '../duck/hooks';
import {
  dashboardSortByNameSel,
  dashboardYouthsByMIDSel,
  mbcProfileGuidSel,
  mbcProfileMIDSel,
  selectedYouthByMBSel,
} from '../duck/selectors';
import { mbcDashboardActions } from '../duck/slice';
import { compareByName } from '../duck/utils';
import styles from './DashboardTabYouth.module.scss';
import MBImage from './MBImage';
import RecordProgressButton from './RecordProgressButton';
import RemoveMBModal from './RemoveMBModal';

interface MBCYouthsByMBViewProps {
  onEdit: (classId: number) => (event: React.MouseEvent) => void;
  onDelete: (classId: number) => (event: React.MouseEvent) => void;
}

export default function MBCYouthsByClassView(
  props: Readonly<MBCYouthsByMBViewProps>,
) {
  const isMobile = useSelector(isMobileSel);

  const { mbClassesDict } = useYouthGroupedByClass();

  const { meritBadgesById } = useMBDict();

  const dispatch = useDispatch();
  const dashboardYouthsByMID = useSelector(dashboardYouthsByMIDSel);
  const selectedYouthByMB = useSelector(selectedYouthByMBSel);
  const sortByName = useSelector(dashboardSortByNameSel);
  const counselorMemberId = useSelector(mbcProfileMIDSel);
  const mbcPersonGuid = useSelector(mbcProfileGuidSel);
  const mbcMid = useSelector(mbcProfileMIDSel) as number;
  const selectedRowKeys = new Set(selectedYouthByMB);

  const [assignToYouth, { isLoading: isAdding }] =
    useAssignMBCToYouthMutation();

  const [removeCounselor, { isLoading: isRemoving }] =
    useRemoveMBCYouthMBMutation();

  const items: CollapseProps['items'] = chain(mbClassesDict)
    .keys()
    .map(mbClassId => {
      const mbcClass = mbClassesDict[mbClassId];
      const mbId = mbcClass.scoutbookId;
      const mbImage = meritBadgesById.get(mbId)?.imageUrl50;
      const selectedYouth = mbcClass.scouts
        .filter(scout => selectedRowKeys.has(scout.rowKey))
        .map(scout => dashboardYouthsByMID[scout.scoutMemberId])
        .filter(youth => !isNil(youth));

      return {
        key: mbClassId,
        label: (
          <Flex>
            <Space align="center">
              <MBImage src={mbImage} width={32} height={32} />
              <Text strong color="color-text-dark" font="body-overline">
                {mbcClass.className} - {mbcClass.meirtBadgeName} (
                {mbcClass.scouts.length})
              </Text>

              <Tooltip title={mbcClass.classDescription}>
                <MdInfo className={styles.infoIcon} />
              </Tooltip>
            </Space>

            <IconButton
              icon={<MdEdit size="small" />}
              className={styles.infoIcon}
              onClick={props.onEdit(mbcClass.counselorClassId)}
              type="text"
              size="small"
            />

            <IconButton
              icon={<MdDelete size="small" />}
              className={styles.deleteIcon}
              onClick={props.onDelete(mbcClass.counselorClassId)}
              type="text"
              size="small"
            />
            <RecordProgressButton
              meritBadgeId={mbId}
              selectedYouth={selectedYouth}
              noSelectionMessageId="mbcDashboard.classTable.recordProgress.noSelection"
            />
          </Flex>
        ),
        title: mbcClass.className,
        children: (
          <Table
            key={sortByName}
            dataSource={mbcClass.scouts}
            pagination={false}
            rowKey={'rowKey'}
            scroll={{ x: true }}
            rowSelection={{
              selectedRowKeys: selectedYouthByMB,
              onChange(selectedRowKeys: React.Key[]) {
                dispatch(
                  mbcDashboardActions.setSelectedYouthByMB({
                    selectedRowKeys: selectedRowKeys as string[],
                    meritBadgeId: mbcClass.meritBadgeId,
                  }),
                );
              },
            }}
            columns={[
              {
                title: (
                  <FormattedMessage id="mbcDashboard.youthTable.column.name" />
                ),
                key: 'name',
                fixed: 'left',
                dataIndex: sortByName,
                defaultSortOrder: 'ascend',
                sorter: (a, b) => compareByName(a, b, sortByName),
                render: (_value, record) => (
                  <Text className={styles.text}>
                    {getPreferredName(record)}
                  </Text>
                ),
              },
              {
                title: (
                  <FormattedMessage id="mbcDashboard.youthTable.column.mid" />
                ),
                minWidth: 120,
                sorter: (a, b) => a.scoutMemberId - b.scoutMemberId,
                dataIndex: 'youth.scoutMemberId',
                render: (_value, record) => (
                  <Text className={styles.text}>{record.scoutMemberId}</Text>
                ),
              },
              {
                title: (
                  <FormattedMessage id="mbcDashboard.youthTable.column.status" />
                ),
                dataIndex: 'status',
                render: (_value, record) => {
                  const youth = dashboardYouthsByMID[record.scoutMemberId];
                  const entry = youth?.meritBadgesCounseling.find(
                    m => m.meritBadgeId === mbcClass.scoutbookId,
                  );
                  return entry ? (
                    <AdvancementStatusTag status={entry.status} />
                  ) : (
                    <Text className={styles.text}>—</Text>
                  );
                },
              },
              {
                title: (
                  <FormattedMessage id="mbcDashboard.youthTable.column.version" />
                ),
                dataIndex: 'youth.version',
                render: (_value, record) => {
                  const youth = dashboardYouthsByMID[record.scoutMemberId];
                  const entry = youth?.meritBadgesCounseling.find(
                    m => m.meritBadgeId === mbcClass.scoutbookId,
                  );

                  return (
                    <Text className={styles.text}>{entry?.version ?? '—'}</Text>
                  );
                },
              },
              {
                title: '',
                width: 40,
                render(_value, record) {
                  const youthName = getPreferredName(record);
                  const direction = isMobile ? 'vertical' : 'horizontal';
                  const youth = dashboardYouthsByMID[record.scoutMemberId];
                  const entry = youth?.meritBadgesCounseling.find(
                    m => m.meritBadgeId === mbcClass.scoutbookId,
                  );
                  const isAssigned = !isNil(entry);

                  return (
                    <Space direction={direction}>
                      <Button
                        icon={<MBImage height={16} src={mbImage} width={16} />}
                        onClick={() => {
                          dispatch(
                            goToAdvancementDetails({
                              advancementId: mbcClass.scoutbookId,
                              advancementType: advancementTypes.MERIT_BADGES,
                              userId: record.scoutSbUserId,
                            }),
                          );
                          dispatch(mbcDashboardActions.setNavigatedToMb(true));
                        }}
                        size="small"
                        type="default"
                      >
                        <FormattedMessage id="mbcDashboard.mbTable.viewMB" />
                      </Button>
                      <Tooltip title={isAssigned ? 'Remove' : 'Assign'}>
                        <Popconfirm
                          title={
                            <FormattedMessage
                              id={
                                isAssigned
                                  ? 'mbcDashboard.mbTable.removeConfirm.title'
                                  : 'mbcDashboard.mbTable.addConfirm.title'
                              }
                            />
                          }
                          description={
                            <FormattedMessage
                              values={{ youthName }}
                              id={
                                isAssigned
                                  ? 'mbcDashboard.mbTable.removeConfirm'
                                  : 'mbcDashboard.mbTable.addConfirm'
                              }
                            />
                          }
                          onConfirm={async () => {
                            if (!counselorMemberId) return;

                            try {
                              if (isAssigned) {
                                await removeCounselor({
                                  body: {
                                    counselorMemberId,
                                    youths: [
                                      {
                                        meritBadges: [+mbId],
                                        youthMemberId: record.scoutMemberId,
                                      },
                                    ],
                                  },
                                  extraTags: [
                                    tagFactory.MBCDashboard.userDashboard(
                                      mbcPersonGuid!,
                                    ),
                                  ],
                                }).unwrap();
                              } else {
                                const payload: MBCYouthAssignationPayload[] = [
                                  {
                                    counselorMemberId,
                                    youths: [
                                      {
                                        youthLastName: record.lastName,
                                        youthMemberId: record.scoutMemberId,
                                        meritBadgesCounseling: [
                                          {
                                            meritBadgeId: mbId,
                                          },
                                        ],
                                      },
                                    ],
                                  },
                                ];

                                await assignToYouth({
                                  payload,
                                  invalidateExtra: [
                                    tagFactory.MeritBadgeCounselorProfile.mbcProfile(
                                      +mbcMid,
                                    ),
                                  ],
                                }).unwrap();
                              }

                              toastService.success(
                                <FormattedMessage
                                  id={
                                    isAssigned
                                      ? 'mbcDashboard.actions.removeYouth.success'
                                      : 'mbcDashboard.actions.addYouth.success'
                                  }
                                />,
                              );
                            } catch (error) {
                              const errorMessage = get(error, [
                                'data',
                                'errorDesc',
                              ]);
                              if (errorMessage) {
                                toastService.error(errorMessage);
                              }
                              return;
                            }
                          }}
                          okButtonProps={{ danger: isAssigned }}
                          cancelButtonProps={{
                            type: 'default',
                            disabled: isRemoving || isAdding,
                          }}
                          onCancel={() => null}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button
                            danger={isAssigned}
                            icon={
                              isAssigned ? (
                                <MdDeleteOutline />
                              ) : (
                                <MdAddCircleOutline />
                              )
                            }
                            onClick={noop}
                            size="small"
                            type="default"
                          >
                            <FormattedMessage
                              id={
                                isAssigned ? 'shared.unassign' : 'shared.assign'
                              }
                            />
                          </Button>
                        </Popconfirm>
                      </Tooltip>
                    </Space>
                  );
                },
              },
            ]}
          />
        ),
      };
    })
    .sortBy('title')
    .value();

  return (
    <>
      <RemoveMBModal />
      <Collapse
        defaultActiveKey={items[0]?.key}
        className={styles.mbCollapse}
        items={items}
      />
    </>
  );
}
